import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ProductItem, productService } from '../../services/product.service';
import ProductCard from '../../components/products/ProductCard';
import {
  Breadcrumb,
  LoadingSpinner,
  NoResults,
  Pagination,
} from '../../components/common';

const SearchPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchQuery = searchParams.get('q') || '';
  const currentPage = parseInt(searchParams.get('page') || '1', 10);

  const [products, setProducts] = useState<ProductItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  const { t } = useTranslation();

  useEffect(() => {
    const fetchSearchResults = async () => {
      setLoading(true);
      try {
        const response = await productService.getAllProducts({
          search: searchQuery,
          page: currentPage,
          limit: 12,
        });

        setProducts(response.products);
        setTotalPages(response.totalPages);
        setTotalProducts(response.total);
      } catch (error) {
        console.error('Error fetching search results:', error);
      } finally {
        setLoading(false);
      }
    };

    if (searchQuery) {
      fetchSearchResults();
    } else {
      setProducts([]);
      setLoading(false);
    }
  }, [searchQuery, currentPage]);

  const handlePageChange = (page: number) => {
    setSearchParams({
      q: searchQuery,
      page: page.toString(),
    });
  };

  const breadcrumbItems = [
    { label: t('common.home'), link: '/' },
    { label: t('search.title'), link: '' },
  ];

  return (
    <div className='container mx-auto px-4 py-8'>
      <Breadcrumb items={breadcrumbItems} />

      <div className='mb-8'>
        <h1 className='text-3xl font-bold text-gray-800 mb-2'>
          {searchQuery
            ? t('search.resultsFor', { query: searchQuery })
            : t('search.title')}
        </h1>
        {totalProducts > 0 && (
          <p className='text-gray-600'>
            {t('search.foundProducts', { count: totalProducts })}
          </p>
        )}
      </div>

      {loading ? (
        <div className='flex justify-center py-12'>
          <LoadingSpinner size='large' />
        </div>
      ) : !searchQuery ? (
        <div className='text-center py-12'>
          <h2 className='text-xl font-medium text-gray-700 mb-4'>
            {t('search.enterQuery')}
          </h2>
          <p className='text-gray-500'>{t('search.startSearching')}</p>
        </div>
      ) : products.length === 0 ? (
        <NoResults
          title={t('search.noResults')}
          message={t('search.tryAgain')}
          buttonText={t('common.allProducts')}
          buttonLink='/products'
        />
      ) : (
        <>
          <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6'>
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>

          {totalPages > 1 && (
            <div className='mt-8'>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SearchPage;
